<template>
  <div class="event-view">
    <loading-view v-if="isLoading" />
    <transition name="slide-y-reverse-transition" mode="out-in">
      <div v-if="!isLoading">
        <div class="sticky-header" v-if="showStickyHeader">
          <div class="event-back-btn" @click="$router.push({ name: 'events' })">
            <i class="material-icons">keyboard_arrow_left</i>
            <span>{{ $t("back") }}</span>
          </div>
          <div>
            <mem-button
              :btnType="'secondary-dark'"
              class="event-btn"
              @click="openMap"
              v-if="showMap"
              >{{ $t("directions") }}</mem-button
            >
            <mem-button
              :btnType="'secondary-dark'"
              class="event-btn"
              @click="onHeroViewTickets"
            >
              <span v-if="volunteerEvent">{{ $t("view_shifts") }}</span>
              <span v-else>{{ $t("view_tickets") }}</span>
            </mem-button>
          </div>
        </div>

        <!-- hero section -->
        <hero-block
          :event="eventData"
          :volunteerEvent="volunteerEvent"
          :showStickyHeader="showStickyHeader"
          :can-share="canShare"
          v-on:open-map="openMap"
          v-on:view-tickets="onHeroViewTickets"
          v-on:share="handleShareDialog"
        />

        <!-- US Army Banner -->
        <div class="army-promo" v-if="showArmyBanner">
          <banner-army @hide-banner="hideArmyBanner" />
        </div>

        <!-- Race details section (new check-in flow) -->
        <div class="race-details-section" v-if="isCheckinFlowEnabled && eventData">
          <div class="race-details-card">
            <div class="event-subheader">{{ $t("race_details_header") }}</div>
            <div class="race-details-grid">
              <div class="detail-block">
                <!-- <i class="material-icons detail-icon" style="color: #cf1019">place</i> -->
                <svg class="detail-icon detail-icon-svg" viewBox="26.3 74.7 17.4 20.6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M41.6667 83.3307C41.6667 87.4916 37.0508 91.8249 35.5008 93.1632C35.3564 93.2718 35.1807 93.3305 35 93.3305C34.8193 93.3305 34.6436 93.2718 34.4992 93.1632C32.9492 91.8249 28.3333 87.4916 28.3333 83.3307C28.3333 81.5626 29.0357 79.8669 30.286 78.6167C31.5362 77.3664 33.2319 76.6641 35 76.6641C36.7681 76.6641 38.4638 77.3664 39.714 78.6167C40.9643 79.8669 41.6667 81.5626 41.6667 83.3307Z" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M35 85.8359C36.3807 85.8359 37.5 84.7166 37.5 83.3359C37.5 81.9552 36.3807 80.8359 35 80.8359C33.6193 80.8359 32.5 81.9552 32.5 83.3359C32.5 84.7166 33.6193 85.8359 35 85.8359Z" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="detail-label">{{ $t("location_label") }}</span>
                <span class="detail-value" :class="{ 'location-link': showMap }" @click="showMap && openMap()">
                  <template v-if="typeof eventData.address === 'string'">{{ eventData.address }}</template>
                  <template v-else-if="eventData.address">
                    <div><small style="color: #9d9d9d">Street:</small> {{ eventData.address.street || '-' }}</div>
                    <div class="address-grid">
                      <div class="address-col">
                        <div><small style="color: #9d9d9d">City:</small> {{ eventData.address.city || '-' }}</div>
                        <div><small style="color: #9d9d9d">State:</small> {{ eventData.address.state || '-' }}</div>
                      </div>
                      <div class="address-col">
                        <div><small style="color: #9d9d9d">Zip:</small> {{ eventData.address.zip || '-' }}</div>
                        <div><small style="color: #9d9d9d">Country:</small> {{ eventData.address.country || '-' }}</div>
                      </div>
                    </div>
                  </template>
                </span>
              </div>
              <div class="detail-block">
                <!-- <i class="material-icons detail-icon" style="color: #cf1019">event</i> -->
                <svg class="detail-icon detail-icon-svg" viewBox="345.5 74.66 19 20.68" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M351.667 76.6641V79.9974" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M358.333 76.6641V79.9974" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M360.833 78.3359H349.167C348.246 78.3359 347.5 79.0821 347.5 80.0026V91.6693C347.5 92.5897 348.246 93.3359 349.167 93.3359H360.833C361.754 93.3359 362.5 92.5897 362.5 91.6693V80.0026C362.5 79.0821 361.754 78.3359 360.833 78.3359Z" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M347.5 83.3359H362.5" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="detail-label">{{ $t("date_time_label") }}</span>
                <span class="detail-value">{{ eventDateFormatted }}</span>
              </div>
              <div class="detail-block">
                <!-- <i class="material-icons detail-icon" style="color: #cf1019">description</i> -->
                <svg class="detail-icon detail-icon-svg" viewBox="26.3 152.7 17.4 20.6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M38.3333 156.331H40C40.442 156.331 40.866 156.506 41.1785 156.819C41.4911 157.131 41.6667 157.555 41.6667 157.997V169.664C41.6667 170.106 41.4911 170.53 41.1785 170.843C40.866 171.155 40.442 171.331 40 171.331H30C29.558 171.331 29.1341 171.155 28.8215 170.843C28.5089 170.53 28.3333 170.106 28.3333 169.664V157.997C28.3333 157.555 28.5089 157.131 28.8215 156.819C29.1341 156.506 29.558 156.331 30 156.331H31.6667M35 162.164H38.3333M35 166.331H38.3333M31.6667 162.164H31.675M31.6667 166.331H31.675M32.5 154.664H37.5C37.9602 154.664 38.3333 155.037 38.3333 155.497V157.164C38.3333 157.624 37.9602 157.997 37.5 157.997H32.5C32.0398 157.997 31.6667 157.624 31.6667 157.164V155.497C31.6667 155.037 32.0398 154.664 32.5 154.664Z" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="detail-label">{{ $t("race_day_program_label") }}</span>
                <span class="detail-value">
                  <template v-if="raceDayProgramUrl">
                    {{ $t("race_day_program_text") }}
                    <a :href="raceDayProgramUrl" target="_blank" class="detail-link">{{ $t("race_day_program_link") }}</a>
                  </template>
                  <template v-else>{{ $t("race_day_program_unavailable") }}</template>
                </span>
              </div>
              <div class="detail-block">
                <!-- <i class="material-icons detail-icon" style="color: #cf1019">send</i> -->
                <svg class="detail-icon detail-icon-svg" viewBox="345.5 152.66 19.8 19.8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M347.5 162.164L363.333 154.664L355.833 170.497L354.167 163.831L347.5 162.164Z" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="detail-label">{{ $t("what_to_bring_label") }}</span>
                <span class="detail-value">{{ $t("what_to_bring_text") }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Check-in section -->
        <checkin-section
          v-if="isCheckinFlowEnabled && !eventData.is_passed && (showCheckinSection || isAllCheckinComplete)"
          :start-date="eventData.start_date"
          :opens-at-date="eventData.checkin_opens_at_time || eventData.checkin_opens_at || ''"
          :checkin-open="eventData.checkin_open"
          :tickets-needing-checkin="eventData.tickets_needing_checkin || 0"
          :all-tickets-confirmed="isAllCheckinComplete"
          :tickets="eventData.tickets || []"
          @start-checkin="handleStartCheckin"
          @view-tickets="handleCheckinViewQr"
        />

        <!-- tickets section -->
        <tickets-list
          ref="raceEntries"
          :tickets="eventData.tickets"
          :event-data="eventData"
          :volunteerEvent="volunteerEvent"
          @start-checkin="handleTicketCheckin"
          @continue-checkin="handleContinueCheckin"
          @view-qr="handleViewQr"
        />

        <!-- teams section -->
        <div class="event-teams" v-if="showTeams">
          <div class="event-subheader">
            {{ $t("your_teams") }}
          </div>

          <template>
            <div class="teams-list">
              <team-card
                v-for="(team, index) in assignedTeams"
                :key="index"
                :item="team"
                disable-route
              >
                <div slot="controls">
                  <mem-button
                    class="button-invite"
                    btn-type="secondary-dark"
                    @click="showInviteDialog(team)"
                  >
                    {{ $t("invite") }}
                  </mem-button>
                </div>
              </team-card>
            </div>
          </template>
        </div>

        <!-- shop section -->
        <div class="shop-section" v-if="showShopSection">
          <div class="section-header">
            <!-- showLimitedDeal -->
            <div class="event-subheader">
              <span v-if="showLimitedDeal">
                {{ $t("event_limited_shop_section_title") }}
              </span>
              <span v-else>{{ $t("event_shop_section_title") }}</span>
            </div>

            <span class="shop-timer" v-if="showLimitedDeal">
              {{ shopTimerFormatted }}
            </span>
          </div>
          <div class="shop-items">
            <product-carousel
              :items="eventData.event_upsell.products"
              :theme="'dark'"
              @on-item-click="pushDataLayerEvent('Merch Upsell Click', $event)"
            />
          </div>
        </div>

        <!-- promo -->
        <div
          class="event-promo"
          v-if="userData.spartan_country_code === 'US' && !volunteerEvent"
        >
          <div class="event-subheader">{{ $t("he_promo_section_title") }}</div>
          <promo-card-he />
        </div>

        <!-- rating secton -->
        <div class="event-ratings" v-if="showRates">
          <div class="ratings-name event-subheader">{{ $t("rating") }}</div>
          <div class="rating-level" v-if="eventData.rates.elevation_gain">
            <rating-level :level="eventData.rates.elevation_gain" />
            <div class="level-name">{{ $t("elevation_gain") }}</div>
          </div>
          <div class="rating-level" v-if="eventData.rates.altitude">
            <rating-level :level="eventData.rates.altitude" />
            <div class="level-name">{{ $t("altitude") }}</div>
          </div>
          <div class="rating-level" v-if="eventData.rates.terrain">
            <rating-level :level="eventData.rates.terrain" />
            <div class="level-name">{{ $t("terrain") }}</div>
          </div>
          <div class="rating-level" v-if="eventData.rates.weather">
            <rating-level :level="eventData.rates.weather" />
            <div class="level-name">{{ $t("weather") }}</div>
          </div>
        </div>

        <!-- Map section -->
        <div class="event-map" v-if="showMap">
          <div class="map-name event-subheader">{{ $t("location") }}</div>
          <mem-map :event="eventData" />
          <!-- <div id="map"></div> -->
          <div class="map-details">
            <div class="map-address">{{ eventData.address?.formatted || eventData.address }}</div>
            <div>
              <mem-button
                :btnType="'tertiary-dark'"
                class="event-btn"
                @click="openMap"
                >{{ $t("directions") }}</mem-button
              >
            </div>
          </div>
        </div>

        <!-- FAQ section -->
        <div class="event-faq">
          <div class="faq-name event-subheader">
            <span>{{ $t("faq") }}</span>
            <mem-button
              v-if="volunteerEvent"
              class="volunteer-button"
              @click="openVolunteerLink"
            >
              {{ $t("learn_more") }}
            </mem-button>
          </div>
          <div
            v-for="(question, index) in eventData.faqs"
            v-bind:key="index"
            class="faq-item"
            :style="{ background: isEven(index) ? '#121212' : '#020000' }"
          >
            <question-line
              :question="question.title"
              :answer="question.description"
              :id="index"
            />
          </div>
        </div>
      </div>
    </transition>
    <v-dialog
      v-model="inviteDialog"
      overlay-color="#000000"
      overlay-opacity="0.54"
      :max-width="480"
      class="mem-dialog"
    >
      <invite-team-dialog
        v-if="inviteDialog"
        :source="'EventPage'"
        :inviteLink="inviteTeamLink"
        :teamTickets="inviteTeamTickets"
        :eventId="eventData.ts_id"
        :eventCountry="eventData.ts_org_code"
        @close-invite="inviteDialog = false"
      />
    </v-dialog>

    <!-- share modal -->
    <v-dialog
      v-model="shareDialog"
      class="mem-dialog"
      overlay-color="#000000"
      overlay-opacity="0.54"
      fullscreen
    >
      <share-dialog
        :key="shareDialogKey"
        :event-name="eventData.name"
        :start-date="eventData.start_date"
        v-on:close="handleShareDialog"
      />
    </v-dialog>

    <checkin-entry-modal
      v-if="isCheckinFlowEnabled"
      v-model="checkinEntryDialog"
      ref="checkinModal"
      :tickets="checkinTickets"
      :event-data="eventData"
      :single-ticket="checkinSingleTicket"
      @select-ticket="handleCheckinSelectTicket"
    />

    <v-dialog
      v-if="isCheckinFlowEnabled"
      v-model="qrDialog"
      overlay-color="#000000"
      overlay-opacity="0.54"
      :max-width="qrDialogWidth"
      class="mem-dialog"
    >
      <qr-ticket-card
        v-if="qrTicket"
        :ticket="qrTicket"
        :index="qrTicketIndex"
        :total="qrTickets.length"
        :eventName="eventData.name"
        :startDate="eventData.start_date"
        :volunteerEvent="volunteerEvent"
      />
    </v-dialog>
  </div>
</template>
<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { uniqBy, map, filter, groupBy, findIndex } from "lodash";
import { KeyConverter } from "@/common/helpers";
import { isUnclaimedPurchase } from "@/common/checkin";
import moment from "moment";

import RatingLevel from "./components/RatingLevel.vue";
import QuestionLine from "./components/QuestionLine.vue";
import HeroBlock from "./components/HeroBlock.vue";
import LoadingView from "./components/LoadingView.vue";
import TeamCard from "@/components/TeamCard";
import BaseButton from "@/components/base/BaseButton";
import InviteTeamDialog from "@/views/Teams/components/InviteTeamDialog";
import { captureFromDataLayer } from "@/plugins/posthog";

export default {
  name: "EventView",
  data: () => ({
    marker: "",
    eventAddress: "",
    heroHeader: "",
    sticky: 45,
    stickyNext: 312,
    showStickyHeader: false,
    isLoading: true,
    inviteDialog: false,
    shareDialog: false,
    shareDialogKey: 0,
    inviteTeamLink: "",
    inviteTeamTickets: [],
    shopTimer: 0,
    shopTimerInterval: "",
    showArmyBanner: false,
    checkinEntryDialog: false,
    checkinSingleTicket: null,
    qrDialog: false,
    qrTicket: null,
  }),
  components: {
    InviteTeamDialog,
    RatingLevel,
    QuestionLine,
    HeroBlock,
    LoadingView,
    TeamCard,
    "mem-button": BaseButton,
    "tickets-list": () => import("./components/TicketsList.vue"),
    "share-dialog": () => import("./components/DialogShareEvent.vue"),
    "mem-map": () => import("@/components/TheMap.vue"),
    "product-carousel": () => import("@/components/shop/ProductCarousel.vue"),
    "promo-card-he": () => import("@/components/banners/BannerHotelEngine.vue"),
    "banner-army": () => import("@/components/banners/BannerArmy.vue"),
    "checkin-section": () => import("./components/CheckinSection.vue"),
    "checkin-entry-modal": () => import("./components/CheckinEntryModal.vue"),
    "qr-ticket-card": () => import("./components/QrTicketCard.vue"),
  },
  computed: {
    ...mapGetters(["eventData", "userData", "checkinSteps", "isCheckinFlowEnabled"]),
    tickets() {
      return this.eventData.tickets;
    },
    qrTickets() {
      return filter(this.eventData.tickets, (ticket) => ticket.status !== "Cancelled" && ticket.checkin_status === "confirmed");
    },
    qrTicketIndex() {
      return findIndex(this.qrTickets, ["barcode", this.qrTicket?.barcode]) + 1;
    },
    qrDialogWidth() {
      const width = this.$vuetify.breakpoint.width;
      if (width > 1024) return 976;
      return width > 375 ? 335 : 351;
    },
    showCheckinSection() {
      if (!this.eventData.start_date) return false;
      return (this.eventData.tickets || []).some((t) => !["transferred", "cancelled"].includes(t.checkin_status));
    },
    checkinTickets() {
      if (!this.eventData.tickets) return [];
      return this.eventData.tickets.filter(
        (t) => (t.checkin_status === "checkin_pending" || t.checkin_status === "unclaimed")
          && !this.isSpectatorTicket(t) && !isUnclaimedPurchase(t)
      );
    },
    isAllCheckinComplete() {
      if (!this.eventData.tickets || !this.eventData.tickets.length) return false;
      const checkinTickets = this.eventData.tickets.filter(
        (t) => (t.checkin_status === "confirmed" || t.checkin_status === "checkin_pending" || t.checkin_status === "unclaimed")
          && !isUnclaimedPurchase(t)
      );
      if (!checkinTickets.length) return false;
      return checkinTickets.every((t) => t.checkin_status === "confirmed");
    },
    eventDateFormatted() {
      if (!this.eventData.start_date) return "";
      return moment(this.eventData.start_date).format("dddd, MMMM D, YYYY");
    },
    raceDayProgramUrl() {
      return this.eventData?.race_day_program_url || "";
    },
    assignedTeams() {
      // filter -> get all tickets with team
      // map -> get only team object
      // uniqBy -> get only uniq teams
      let uniqTeams = uniqBy(
        map(
          filter(this.tickets, (ticket) => ticket.team && ticket.team.joined),
          "team"
        ),
        "id"
      );

      return map(uniqTeams, (team) => {
        // TODO: this is suck
        let newTeam = KeyConverter.camelize(team);
        newTeam.tickets = this.teamRegChoices[team.id];
        return newTeam;
      });
    },
    teamRegChoices() {
      // reg_choice_name
      return groupBy(filter(this.tickets, "team"), "team.id");
    },
    showShopSection() {
      if (this.volunteerEvent) return false;
      let { event_upsell } = this.eventData;
      if (!event_upsell) return;
      return event_upsell.products.length > 0;
      // return this.shopTimer > 0;
    },
    showLimitedDeal() {
      return this.shopTimer > 0;
    },
    showTeams() {
      return this.eventData && this.assignedTeams.length !== 0;
    },
    showMap() {
      let { address, latitude, longitude } = this.eventData;
      const hasAddress = typeof address === "string" ? address : address?.formatted;
      return hasAddress || (latitude && longitude);
    },
    displayAddress() {
      const addr = this.eventData?.address;
      if (!addr) return "";
      if (typeof addr === "string") return addr;
      const street = addr.street && addr.street !== this.eventData.venue_name ? addr.street : "";
      return [street, addr.city, addr.state, addr.zip]
        .filter(Boolean)
        .join(", ");
    },
    showRates() {
      let { rates } = this.eventData;
      if (!rates) return;
      return (
        rates.elevation_gain || rates.altitude || rates.terrain || rates.weather
      );
    },
    canShare() {
      if (!this.isMobile) return;
      if (!navigator.share) return;

      let { tickets, ts_org_code } = this.eventData;
      let kidTicketIndex = findIndex(tickets, "is_kids");

      if (kidTicketIndex !== -1 || ts_org_code !== "US") return;

      return true;
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    },
    shopTimerFormatted() {
      // const timeZero = moment("1900-01-01 00:00:00");
      // return `${this.$t("event_shop_section_timer")} ${timeZero
      //   .add(this.shopTimer, "seconds")
      //   .format("mm[m] ss[s]")}`;
      let minutes = ~~(this.shopTimer / 60); // ~~ is a shorthand for Math.floor
      let seconds = this.shopTimer - minutes * 60;
      return `${this.$t("event_shop_section_timer")} ${minutes}m ${seconds}s`;
    },
    eventDate() {
      return moment(this.eventData.start_date).format("MMMM D");
    },
    bannerTarget() {
      if (this.volunteerEvent) return false;
      if (this.userData.spartan_country_code !== "US") return false;
      if (!this.userData.birth_date) return false;

      let userAge = moment().diff(moment(this.userData.birth_date), "year");
      return userAge >= 16 && userAge <= 28;
    },
    volunteerEvent() {
      return this.eventData.ts_org_code === "VOL";
    },
  },
  methods: {
    ...mapMutations(["setCurrentTicket"]),
    ...mapActions(["getEvent", "getCheckinSteps"]),
    isSpectatorTicket(ticket) {
      return ticket?.is_spectator === true || /spectator/i.test(ticket?.reg_choice_name || "");
    },
    handleStartCheckin() {
      if (!this.checkinTickets.length) {
        // Only spectators left: open the first one's form directly
        const spectator = (this.eventData.tickets || []).find(
          (t) => (t.checkin_status === "checkin_pending" || t.checkin_status === "unclaimed")
            && this.isSpectatorTicket(t) && !isUnclaimedPurchase(t)
        );
        if (spectator) this.handleCheckinSelectTicket(spectator);
        return;
      }
      this.checkinSingleTicket = null;
      this.checkinEntryDialog = true;
    },
    handleTicketCheckin(ticket) {
      // Spectators have no wave time: open the form directly, no "Confirm wave time" modal
      if (this.isSpectatorTicket(ticket)) {
        this.handleCheckinSelectTicket(ticket);
        return;
      }
      this.checkinSingleTicket = ticket;
      this.checkinEntryDialog = true;
    },
    async handleContinueCheckin(ticket) {
      try {
        await this.getCheckinSteps(ticket.barcode);
      } catch (error) {
        console.log("Get checkin steps error: ", error);
      }

      const params = ticket.checkin_params || {};
      const isKid = ticket.is_kids === true;
      const steps = isKid
        ? (this.checkinSteps.kids && this.checkinSteps.kids.steps) || []
        : this.checkinSteps.steps || [];
      const keys = [].concat(...steps.map((s) => s.key));
      const hasPhone = !keys.length || keys.includes("phone");
      const biometricRequired = (!keys.length && !isKid) || keys.includes("biometric_marking_consent") || keys.includes("biomarking_consent");
      const biometricAnswered = params.biometric_marking_consent === true || params.biometric_marking_consent === false;

      let step;
      if (isKid) {
        step = "kids-name";
        if (params.first_name && !params.t_shirt) step = "kids-tshirt";
        else if (params.t_shirt && !params.birth_date) step = "kids-dob";
        else if (params.birth_date && !params.gender) step = "kids-gender";
        else if (params.gender && biometricRequired && !biometricAnswered) step = "biometric";
      } else if (
        ticket.is_purchased_by_me && !ticket.is_claimed && !(ticket.checkin_finished_step > 0)
        && !params.t_shirt && !params.birth_date && !params.gender
        // Bought with a pass: only the purchaser can claim it, so no "for me / send to a friend" choice
        && ticket.pass_type !== "non-transferrable"
      ) {
        // Never assigned after purchase: ask "for me / send to a friend" first, not the racer wizard
        step = "ownership";
      } else {
        step = "tshirt";
        if (params.t_shirt && !params.birth_date) step = "dob";
        else if (params.birth_date && hasPhone && !params.phone) step = "phone";
        else if (params.birth_date && (params.phone || !hasPhone) && !params.gender) step = "gender";
        else if (params.gender && biometricRequired && !biometricAnswered) step = "biometric";
      }

      this.$router.push({
        name: "checkout",
        params: { region: ticket.country?.toLowerCase() || "us" },
        query: {
          resume: "true",
          barcode: ticket.barcode,
          step,
          ...(isKid ? { kid: "true" } : {}),
        },
      });
    },
    handleCheckinSelectTicket(ticket) {
      this.checkinEntryDialog = false;
      this.setCurrentTicket(ticket);
      this.$router.push({
        name: this.volunteerEvent ? "volunteerTickets" : "tickets",
        params: {
          id: this.$route.params.id,
          barcode: ticket.barcode,
          type: "change-information",
        },
      });
    },
    handleCheckinViewQr() {
      if (this.qrTickets.length === 1) return this.handleViewQr(this.qrTickets[0]);
      this.viewTickets();
    },
    handleViewQr(ticket) {
      this.qrTicket = ticket;
      this.qrDialog = true;
    },
    isEven(index) {
      return index % 2 === 0;
    },
    openMap() {
      const addr = typeof this.eventData?.address === "string"
        ? this.eventData.address
        : this.eventData?.address?.formatted;
      if (addr)
        return window.open(`https://maps.google.com/?q=${addr}`);
      return window.open(
        `https://www.google.com/maps/search/?api=1&query=${this.eventData.latitude},${this.eventData.longitude}`
      );
    },
    handleShareDialog() {
      if (!this.shareDialog) {
        this.shareDialogKey += 1;
        this.shareDialog = true;
      } else {
        this.shareDialog = false;
      }
    },
    onHeroViewTickets() {
      if (!this.isCheckinFlowEnabled) return this.viewTickets();
      // New check-in flow: tickets are on this page, scroll down to Race Entries
      const el = this.$refs.raceEntries && this.$refs.raceEntries.$el;
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    viewTickets() {
      this.$router.push({
        name: this.volunteerEvent ? `volunteerEventTickets` : `eventTickets`,
        params: { id: this.$route.params.id },
      });
    },
    scrollPage() {
      if (!document.getElementById("hero-header") || window.innerWidth <= 499)
        return;
      else {
        if (!this.heroHeader)
          this.heroHeader = document.getElementById("hero-header");
        if (window.pageYOffset >= this.sticky) {
          this.heroHeader.classList.add("shadow");
        } else {
          this.heroHeader.classList.remove("shadow");
        }
        if (window.pageYOffset >= this.stickyNext) {
          this.showStickyHeader = true;
        } else {
          this.showStickyHeader = false;
        }
      }
    },
    showInviteDialog(listTeam) {
      this.inviteTeamLink = `Hey, I invite you to join my team ${listTeam.name} at ${this.eventData.name} on ${this.eventDate}.\nBuy the ticket using the link below and join my team after the purchase.\n`;

      // let eventId = this.$route.params.id;
      // this.inviteTeamLink = `${window.location.origin}/join-race?event_id=${eventId}&team_id=${listTeam.id}`;
      this.inviteTeamTickets = listTeam.tickets;

      // if (!listTeam.isPublic && listTeam.inviteCode) {
      //   this.inviteTeamLink =
      //     this.inviteTeamLink + `&invite_code=${listTeam.inviteCode}`;
      // }
      this.inviteDialog = true;
    },
    runShopTimer(duration) {
      if (!duration) return;
      if (this.shopTimerInterval) clearInterval(this.shopTimerInterval);

      this.shopTimer = duration;
      console.log("Start timer. Duration: ", duration);

      // if (!this.shopTimer) return clearInterval(this.shopTimerInterval);

      this.shopTimerInterval = setInterval(() => {
        if (this.shopTimer === 0) return clearInterval(this.shopTimerInterval);
        this.shopTimer--;
      }, 1000);
    },
    pushDataLayerEvent(event, data) {
      let payload = {
        event,
        eventId: this.eventData.id,
        eventName: this.eventData.name,
        eventStartDate: this.eventData.start_date,
      };
      if (data) payload.productId = data.id;

      captureFromDataLayer(event, payload);

      if (!window.dataLayer) return;

      return window.dataLayer.push(payload);
    },
    hideArmyBanner() {
      console.log("Hide banner");
      localStorage.setItem("hideArmyPromoBanner", true);
      this.showArmyBanner = false;
    },
    openVolunteerLink() {
      return window.open(
        "https://spartanrace.zendesk.com/hc/en-us/categories/20516295553815-VOLUNTEERS",
        "_self"
      );
    },
  },
  async mounted() {
    try {
      await this.getEvent(this.$route.params.id);
      if (this.bannerTarget && !localStorage.getItem("hideArmyPromoBanner")) {
        this.showArmyBanner = true;
      }
      // shop section
      let { expires } = this.eventData.event_upsell || {};
      this.runShopTimer(expires);

      // share modal
      let { showShareModal } = this.$route.params;
      if (showShareModal && this.canShare) this.handleShareDialog();
    } catch (error) {
      console.log(error, "");
    }
    this.isLoading = false;
  },
  watch: {
    showShopSection: {
      handler(val) {
        if (val) this.pushDataLayerEvent("Merch Upsell Visible");
      },
    },
    showLimitedDeal: {
      handler(status, oldStatus) {
        if (!oldStatus) return;

        this.$nextTick(async () => {
          await this.getEvent(this.$route.params.id);
        });
      },
    },
  },
  created() {
    window.addEventListener("scroll", this.scrollPage, { passive: true });
  },
  destroyed() {
    window.removeEventListener("scroll", this.scrollPage, { passive: true });
    clearInterval(this.shopTimerInterval);
  },
};
</script>
<style lang="scss" scoped>
.ticket-menu {
  height: 240px;
  width: 278px;

  background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.1)
    ),
    #000000;
  /* Grey 3 */
  border: 0.5px solid #4f4f4f;
  box-sizing: border-box;
  box-shadow: 0px 10px 14px rgba(0, 0, 0, 0.5);
  border-radius: 18px;

  padding: 12px;

  .menu-item {
    height: 55px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 14px;
    color: #ffffff;

    display: flex;
    flex-direction: row;
    align-items: center;

    padding: 0 0 0 15px;

    transform: perspective(1px) translateZ(0);
    transition-duration: 0.5s;

    i {
      color: rgba(255, 255, 255, 0.4);
      margin-right: 18px;
    }
  }

  .menu-item:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: scale(1.03);

    i {
      color: #ffffff;
      transition: color 0.5s linear;
    }

    // transition: background-color 0.2s linear;
  }
}

.ticket-mobile-dialog {
  background: #ffffff;
  border-radius: 5px;
  padding: 25px 0 25px 25px;

  color: #000000;

  .dialog-menu-item {
    font-size: 14px;
    font-weight: bold;
    line-height: 14px;
    text-transform: uppercase;
    height: 44px;
    display: flex;
    flex-direction: row;
    align-items: center;

    i {
      color: #c0c0c0;
      margin-right: 14px;
    }
  }
}

.event-view {
  max-width: min(670px, 100vw);
  width: 100%;
  .event-subheader {
    font-size: 24px;
    line-height: 28px;
    font-weight: bold;
    @media screen and (max-width: 499px) {
    }
    @media screen and (max-width: 375px) {
      font-size: 22px;
    }
  }

  .event-btn {
    height: 36px;
    font-size: 12px;
    line-height: 15px;
    letter-spacing: 0.04em;
    padding: 0 20px;
  }

  // Hero VIEW TICKETS scrolls here: stop below the toolbar (65px) + sticky header (85px)
  .tickets-list {
    scroll-margin-top: 150px;
    @media screen and (max-width: 499px) {
      scroll-margin-top: 65px;
    }
  }
  .sticky-header {
    background: #000000;
    position: sticky;
    top: 65px;
    height: 85px;
    z-index: $zindex-page-sticky-el;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 40px;
    @media screen and (max-width: 499px) {
      display: none;
    }
    .event-back-btn {
      display: flex;
      flex-direction: row;
      align-items: center;
      text-transform: uppercase;
      position: relative;
      @include cursorPointer;
      i {
        position: absolute;
        font-size: 30px;
        left: -10px;
      }
      span {
        font-size: 14px;
        line-height: 14px;
        font-weight: bold;
        margin-left: 20px;
      }
    }
    .event-btn {
      height: 35px;
    }
    .event-btn:first-child {
      margin-right: 12px;
    }
  }
  .army-promo {
    margin-top: 12px;
    @media screen and (max-width: $mobile) {
      margin-top: 16px;
    }
  }
  .event-promo {
    @include elementMarginBottom("M");
    .event-subheader {
      @include elementMarginBottom("S");
      border-top: 1px solid #333333;
      padding-top: 14px;
      @media screen and (max-width: $mobile) {
        margin-left: 16px;
        margin-right: 16px;
      }
    }
  }

  .shop-section {
    @include elementMarginBottom("M");
    @media screen and (max-width: $mobile) {
      padding-left: 20px;
      padding-right: 20px;
    }
    .section-header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding-top: 14px;
      padding-bottom: 20px;
      border-top: 1px solid #333333;
      .shop-timer {
        font-size: 16px;
        line-height: 20px;
        font-weight: 700;
        letter-spacing: 0.0025em;
        text-transform: capitalize;
        color: #000000;
        background-color: #2dff4e;
        padding: 0 4px;
        margin-top: 10px;
      }
    }
    .shop-items {
      min-height: 378px;
    }
  }

  .event-tickets {
    padding: 50px 0;
    @media screen and (max-width: 375px) {
      padding: 37px 0 50px 0;
    }
  }

  .event-teams {
    margin-bottom: 72px;
    .event-subheader {
      margin-bottom: 20px;
      @media screen and (max-width: 499px) {
        padding: 0 16px;
      }
    }
    .teams-list {
      .button-invite {
        margin-right: 24px;
      }
    }
  }

  .event-ratings {
    width: 325px;
    padding-bottom: 50px;
    @media screen and (max-width: 499px) {
      width: 100%;
      padding: 0 20px 36px 20px;
    }
    @media screen and (max-width: 375px) {
      padding: 0 16px 36px 16px;
    }
    .ratings-name {
      border-top: 1px solid rgba(255, 255, 255, 0.2);
      padding: 15px 0 30px 0;
      @media screen and (max-width: 499px) {
        padding: 16px 0 24px 0;
      }
    }
    .rating-level {
      display: flex;
      flex-direction: row;
      align-items: center;

      margin-bottom: 14px;
      .level-name {
        font-weight: bold;
        font-size: 14px;
        line-height: 17px;
        text-transform: capitalize;
        margin-left: 20px;
      }
    }
  }

  .event-map {
    @media screen and (max-width: 499px) {
      padding-left: 20px;
      padding-right: 20px;
    }
    @media screen and (max-width: 375px) {
      padding-left: 16px;
      padding-right: 16px;
    }

    .map-name {
      border-top: 1px solid rgba(255, 255, 255, 0.2);
      padding: 15px 0 24px 0;
      @media screen and (max-width: 499px) {
        padding: 16px 0 24px 0;
      }
    }
    #map {
      height: 240px;
      max-width: 670px;
      width: 100%;
      @media screen and (max-width: 375px) {
        height: 226px;
      }
    }
    .map-details {
      padding: 30px 0 53px 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      .map-address {
        font-weight: 600;
        font-size: 14px;
        line-height: 17px;
        letter-spacing: 0.0025em;
        text-transform: capitalize;
        max-width: 220px;
      }
    }
  }

  .event-faq {
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    margin-bottom: 100px;
    @media screen and (max-width: 499px) {
      padding-left: 20px;
      padding-right: 20px;
    }
    @media screen and (max-width: 375px) {
      padding-left: 0;
      padding-right: 0;
    }
    .faq-name {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      border-top: 1px solid rgba(255, 255, 255, 0.2);
      padding: 15px 0 30px 0;
      @media screen and (max-width: 499px) {
        padding: 16px 0 24px 0;
      }
      @media screen and (max-width: 375px) {
        margin: 0 16px;
      }
      .volunteer-button {
        height: 37px;
        padding: 0 16px;
        font-size: 12px;
        font-weight: 700;
        line-height: normal;
        letter-spacing: 0.48px;
        text-transform: uppercase;
      }
    }
  }
  .race-details-section {
    margin-top: 32px;
    margin-bottom: 32px;

    .race-details-card {
      background: #1D1D1D;
      border: 1px solid #121212;
      border-radius: 4px;
      padding: 24px 24px 0px;
      box-sizing: border-box;
      @media screen and (max-width: 499px) {
        padding: 20px;
      }
    }

    .event-subheader {
      font-family: "Montserrat", sans-serif;
      font-weight: 600;
      font-size: 24px;
      line-height: 32px;
      color: #ffffff;
      margin-bottom: 20px;
      @media screen and (max-width: 499px) {
        font-size: 20px;
        line-height: 28px;
        margin-bottom: 12px;
      }
    }

    .race-details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 20px;
      row-gap: 20px;
      padding-bottom: 24px;
      @media screen and (max-width: 499px) {
        grid-template-columns: 1fr;
        gap: 16px;
        padding-bottom: 0;
      }
    }

    .detail-block {
      display: grid;
      grid-template-columns: 20px 1fr;
      column-gap: 12px;
      row-gap: 4px;
      @media screen and (max-width: 499px) {
        column-gap: 16px;
      }
    }

    .detail-icon {
      grid-column: 1;
      grid-row: 1;
      align-self: start;
      font-size: 20px;
    }

    .detail-icon-svg {
      width: 20px;
      height: 20px;
      color: #cf1019;
      display: block;
    }

    .detail-label {
      grid-column: 2;
      grid-row: 1;
      align-self: start;
      font-family: "Montserrat", sans-serif;
      font-size: 12px;
      font-weight: 600;
      line-height: 18px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #979797;
    }

    .detail-value {
      grid-column: 2;
      grid-row: 2;
      font-family: "Montserrat", sans-serif;
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
      color: #ffffff;

      .address-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 4px 16px;
        margin-top: 4px;
      }
    }

    .location-link {
      text-decoration: underline;
      cursor: pointer;
      &:hover { opacity: 0.8; }
    }

    .detail-link {
      color: #cf1019;
      text-decoration: underline;
      &:hover { opacity: 0.8; }
    }
  }
}
</style>
